// src/pages/quotations/QuotationList.jsx

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchQuotations, QUOTATION_STATUS } from "../../services/quotationService";

export default function QuotationList() {
  const navigate   = useNavigate();
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";

  const [quotations, setQuotations] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState("all");
  const [search,     setSearch]     = useState("");

  const [summary, setSummary] = useState({
    total: 0, draft: 0, sent: 0, accepted: 0, total_value: 0,
  });

  useEffect(() => { if (company_id) loadAll(); }, [company_id]); // eslint-disable-line

  const loadAll = async () => {
    try {
      setLoading(true);
      const data = await fetchQuotations(company_id);
      setQuotations(data);

      let total_value = 0, draft = 0, sent = 0, accepted = 0;
      data.forEach(q => {
        if (q.status === "superseded") return; // ✅ skip
        total_value += Number(q.total_amount || 0);
        if (q.status === "draft")    draft++;
        if (q.status === "sent")     sent++;
        if (q.status === "accepted") accepted++;
      });
      setSummary({ total: data.length, draft, sent, accepted, total_value });
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const filtered = quotations.filter(q => {
    const matchFilter = filter === "all" || q.status === filter;
    const matchSearch = !search ||
      q.quotation_no?.toLowerCase().includes(search.toLowerCase()) ||
      q.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      q.subject?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-6xl mx-auto">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Quotations</h1>
          <p className="text-sm text-gray-500 mt-0.5">{summary.total} quotations</p>
        </div>
        <button
          onClick={() => navigate("/quotations/create")}
          className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700">
          + New Quotation
        </button>
      </div>

      {/* SUMMARY */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Value",   val: `₹${summary.total_value.toLocaleString("en-IN")}`, color: "bg-gray-800 text-white" },
          { label: "Draft",         val: summary.draft,    color: "bg-gray-100 text-gray-700" },
          { label: "Sent",          val: summary.sent,     color: "bg-blue-50 text-blue-700" },
          { label: "Accepted",      val: summary.accepted, color: "bg-green-50 text-green-700" },
        ].map(c => (
          <div key={c.label} className={`rounded-xl p-4 ${c.color}`}>
            <div className="text-2xl font-bold">{c.val}</div>
            <div className="text-xs mt-1 opacity-70">{c.label}</div>
          </div>
        ))}
      </div>

      {/* FILTERS */}
      <div className="flex flex-col md:flex-row gap-3">
        <input
          placeholder="Search by quote no, client, subject..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border p-2 rounded-xl text-sm w-full md:w-72 outline-none"
        />
        <div className="flex gap-2 flex-wrap">
          {["all", ...Object.keys(QUOTATION_STATUS)].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition ${
                filter === s
                  ? "bg-gray-800 text-white"
                  : "bg-white border border-gray-200 text-gray-600"
              }`}>
              {s === "all" ? "All" : QUOTATION_STATUS[s]?.label}
            </button>
          ))}
        </div>
      </div>

      {/* LIST */}
      {loading ? (
        <p className="text-gray-400 text-center py-10">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">📄</div>
          <p className="font-semibold">No quotations found</p>
          <button
            onClick={() => navigate("/quotations/create")}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold">
            Create First Quotation
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(q => {
            const statusInfo = QUOTATION_STATUS[q.status] || QUOTATION_STATUS.draft;
            return (
              <div key={q.id}
                onClick={() => navigate(`/quotations/${q.id}`)}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md cursor-pointer transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-gray-800">{q.quotation_no}</span>
                      {q.revision > 0 && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">
                          {q.revision_label}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-700 mt-1">
                      {q.subject || q.project_type || "—"}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {q.client_name}
                      {q.site_name && ` — ${q.site_name}`}
                    </p>
                    {q.validity_date && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Valid till: {q.validity_date}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-bold text-gray-800">
                      ₹{Number(q.total_amount || 0).toLocaleString("en-IN")}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {q.items?.length || 0} items
                    </div>
                    {q.converted_to_project && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold mt-1 block">
                        ✅ Converted
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}